import Routes from "./Routes";
import i18n from "i18next";
import { YbugProvider } from "ybug-react";

import "moment/locale/es";
import "moment/locale/pt";
import "moment/locale/hi";
import "moment/locale/zh-cn";

import "react-tooltip/dist/react-tooltip.css";

import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { rgbaObjectToString } from "./helpers/utils";
import { TOAST_BG_COLOR, TOAST_TEXT_COLOR } from "./helpers/constants";
import { useEffect, useMemo } from "react";
import useTheme from "./hooks/useTheme";
import { BrowserRouter as Router } from "react-router-dom";
import useMobile from "./hooks/useMobile";
import { saveLanguage } from "./helpers/language";

import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { initDeferredPwaPrompt } from "./helpers/pwaInstallPrompt";

initDeferredPwaPrompt();

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: "https://26b3de749a509d12c5a6b4eaf027761d@o4509703447969792.ingest.us.sentry.io/4509703473070080",
    tracesSampleRate: 1.0,
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

i18n.on("languageChanged", (lng) => {
  saveLanguage(lng);
});

function App() {
  const { updateThemeMetaTags } = useTheme();
  const { isMobile } = useMobile();
  const { i18n } = useTranslation();

  const preventShake = (e: DeviceMotionEvent) => {
    e?.preventDefault?.();
  };

  useEffect(() => {
    window.addEventListener("devicemotion", preventShake);
    return () => window.removeEventListener("devicemotion", preventShake);
  }, []);

  useEffect(() => {
    updateThemeMetaTags();
  }, []);

  const ybugLanguage = useMemo(() => {
    return i18n.resolvedLanguage?.split("-")[0] || "en";
  }, [i18n.resolvedLanguage]);

  return (
    <>
      {/* <div className={clsx(["fixed", "inset-0", "z-[-999]", "md:hidden"])} /> */}

      <Toaster
        position={isMobile ? "bottom-center" : "top-center"}
        containerStyle={{
          bottom: isMobile ? 110 : 20,
        }}
        key={`mobile-${isMobile}`}
        reverseOrder
        toastOptions={{
          duration: 3000,
          style: {
            backgroundColor: rgbaObjectToString(TOAST_BG_COLOR),
            color: rgbaObjectToString(TOAST_TEXT_COLOR),
            fontSize: isMobile ? 12 : 13,
            fontFamily: "Roboto",
            pointerEvents: "none",
          },
        }}
      />
      <QueryClientProvider client={queryClient}>
        <YbugProvider
          ybugId="j5w7q768ajzwt9yxhgk0"
          key={`ybug-${ybugLanguage}-${isMobile ? "mobile" : "desktop"}`}
          settings={{
            email_required: false,
            launcher_position: isMobile ? "right-middle" : "bottom-right",
            language_override: ybugLanguage,
            widget_position: "center",
            comment_required: true,
            priority: false,
            console_log: true,
          }}
        >
          <Router
            future={{
              v7_relativeSplatPath: true,
              v7_startTransition: true,
            }}
          >
            <Routes />
          </Router>
        </YbugProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;
